import type { HabitWeekReport, WeeklyReviewView } from '@contracts';

/**
 * Verdict panel (§1.2) — the lead. Tone-band headline (strong/solid/
 * stretched), identical chrome across all three bands (celebrate-wins-never-
 * punish applied to the CARD itself, not just the copy — no hue ever
 * changes, only the sentence). Three stat blocks: completed, allowances
 * kept, study time (always "—" this round, WR-3 layout reservation).
 */
export function VerdictPanel({
  review,
  habits,
  completed,
}: {
  readonly review: WeeklyReviewView;
  readonly habits: readonly HabitWeekReport[];
  readonly completed: boolean;
}) {
  const buildHabits = habits.filter((h) => h.habitKind === 'build');
  const avoidHabits = habits.filter((h) => h.habitKind === 'avoid');

  const plannedTotal = buildHabits.reduce((sum, h) => sum + h.current.planned, 0);
  const doneTotal = buildHabits.reduce((sum, h) => sum + Math.min(h.current.actual, h.current.planned), 0);

  const allowancesKept = avoidHabits.filter((h) => h.current.met).length;

  const ratio = plannedTotal > 0 ? doneTotal / plannedTotal : null;
  const hasSuggestions = habits.some((h) => h.suggestion !== null);

  const headline = review.available
    ? headlineFor(ratio, doneTotal, plannedTotal, habits.length === 0)
    : "This week's numbers, so far — the verdict lands Sunday.";

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
      }}
    >
      <p style={{ margin: 0, fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
        {headline}
      </p>

      {completed && (
        <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Review done.
        </p>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-6)', marginTop: 'var(--space-5)', flexWrap: 'wrap' }}>
        <StatBlock
          value={plannedTotal > 0 ? `${doneTotal}/${plannedTotal}` : '—'}
          label="completed"
        />
        <StatBlock
          value={avoidHabits.length > 0 ? `${allowancesKept} of ${avoidHabits.length}` : '—'}
          label="allowances kept"
        />
        <StatBlock value="—" label="study time" />
      </div>

      {hasSuggestions && (
        <p style={{ margin: 'var(--space-4) 0 0', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Some habits look over-planned — there are suggestions below.
        </p>
      )}
    </section>
  );
}

function headlineFor(ratio: number | null, done: number, planned: number, noHabits: boolean): string {
  if (noHabits) return 'Objectives moved this week — see below.';
  if (ratio === null) return 'Your first review. From here on, this is where the plan meets the week.';
  if (ratio >= 0.85) return `A strong week: ${done} of ${planned} planned things done.`;
  if (ratio >= 0.6) return `A solid week: ${done} of ${planned} planned things done.`;
  return `A stretched week: ${done} of ${planned} — the plan and the week disagreed. That's what adjustments are for.`;
}

function StatBlock({ value, label }: { readonly value: string; readonly label: string }) {
  return (
    <div>
      <div
        className="num"
        style={{
          fontSize: 'var(--type-display-size)',
          fontWeight: 'var(--type-display-weight)',
          color: 'var(--text-primary)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 'var(--space-1)',
          fontSize: 'var(--type-label-size)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
      </div>
    </div>
  );
}
